import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import CaretDown24 from "../../../../assets/svg/icons/common/CaretDown24";
import CheckGreenCircleIcon from "../../../../assets/svg/icons/common/check-green-circle";
import BrandItem from "../../../../components/brand";
import EquipmentItem from "../../../../components/equipment";
import TextInput from "../../../../components/form/text-input";
import HeadingWithLine from "../../../../components/HeadingWithLine/HeadingWithLine";
import useEquipmentQuantity from "../../../../hooks/useEquipmentQuantity";
import baseStyles from "../../style.module.scss";
import styles from "./styles.module.scss";
import { useEquipmentsByTypeAndBrand } from "./useEquipmentsByTypeAndBrand";
import useUserStore from "../../../../stores/user";

const getPrettyEquipmentType = (name) => {
  switch (name) {
    case "add_on":
      return "Add ons";
    case "termial":
      return "Mobile Systems and Terminals";
    case "gateway":
      return "Gateways";
    default:
      return name;
  }
};

const Header = ({ isOpen, onToggle, hasErrors }) => {
  return (
    <div className={baseStyles.sectionHeader} onClick={onToggle}>
      <div
        className={`${baseStyles.sectionHeaderLeft} ${isOpen && baseStyles.open}`}
      >
        <CaretDown24 />
        <h6 className={baseStyles.sectionTitle}>Equipments</h6>
      </div>
      <div className={baseStyles.sectionHeaderRight}>
        <div className={baseStyles.sectionHeaderRight}>
          {!hasErrors && <CheckGreenCircleIcon />}
        </div>
      </div>
    </div>
  );
};

const Equipments = ({ isOpen, onToggle }) => {
  const { values, errors, touched, handleChange, setFieldValue } =
    useFormikContext();
  const { handleAddEquipment } = useEquipmentQuantity();
  const { equipments, groupedEquipments } = useEquipmentsByTypeAndBrand();
  const [equipmentBeingConfigured, setEquipmentBeingConfigured] =
    useState(null);
  const user = useUserStore((state) => state.user);
  const nonEditableTemplateOrPricingRestricted =
    values.nonEditableTemplateApplied ||
    user?.organization?.pricing_restricted === 1;
  const hasErrors = errors?.equipments || values?.equipments?.length === 0;

  const handleAddEquipmentForConfig = (equipmentId) => {
    setEquipmentBeingConfigured(equipments.find((e) => e.id === equipmentId));
  };

  const handleEquipmentChange = (updatedEquipment) => {
    setEquipmentBeingConfigured(updatedEquipment);
  };

  const handleAddConfiguredEquipment = (type, quantity) => {
    if (type === "apply_tpp") {
      quantity = 1;
    }

    const equipmentToAdd = {
      ...equipmentBeingConfigured,
      type,
      quantity,
    };

    if (equipmentBeingConfigured?.sim_card == 1) {
      equipmentToAdd.sim_card_default = "yes";
    }

    handleAddEquipment(equipmentToAdd);
    setEquipmentBeingConfigured(null);
  };

  const handleRemoveAllEquipments = async (equipmentIds, brandId) => {
    const newEquipments = values.equipments.filter(
      (equipment) => !equipmentIds.includes(equipment.id),
    );
    setFieldValue("equipments", newEquipments);
  };

  // Transform and set equipment data for review component
  useEffect(() => {
    if (!values.equipments?.length) return;

    const selectedEquipments = values.equipments
      .map((id) => equipments.find((eq) => eq.id === id))
      .filter(Boolean);

    const purchases = [];
    const monthly = [];
    const tpp = [];

    selectedEquipments.forEach((equipment) => {
      const baseEquipment = {
        model: equipment.name,
        quantity: 1,
        image: `/get-image-by-path?path=${equipment.image}`,
      };

      if (equipment.purchase_price) {
        purchases.push({
          ...baseEquipment,
          price: equipment.purchase_price,
        });
      }

      if (equipment.monthly_cost) {
        monthly.push({
          ...baseEquipment,
          monthly_cost: equipment.monthly_cost,
          type: equipment.rental_type || "Rent", // or could be 'Lease'
        });
      }

      if (equipment.tpp_price) {
        tpp.push({
          ...baseEquipment,
          price: equipment.tpp_price,
        });
      }
    });

    setFieldValue("equipment_purchases", purchases);
    setFieldValue("equipment_monthly", monthly);
    setFieldValue("equipment_tpp", tpp);
  }, [values.equipments, equipments]);

  if (!isOpen) {
    return (
      <div className={baseStyles.section}>
        <Header isOpen={isOpen} onToggle={onToggle} hasErrors={hasErrors} />
      </div>
    );
  }

  return (
    <div className={`${baseStyles.section} equipmentSection`}>
      <Header isOpen={isOpen} onToggle={onToggle} hasErrors={hasErrors} />

      {errors?.equipments && touched?.equipments && (
        <div className={styles.content}>
          <p className={styles.error}>{errors.equipments}</p>
        </div>
      )}

      <div className={styles.equipmentsContainer}>
        {groupedEquipments.map((group) => (
          <div
            key={`group-${group.name}`}
            className={styles.brandContainerItem}
          >
            <HeadingWithLine
              text={getPrettyEquipmentType(group.name)}
              className={styles.categoryTitle}
            />
            <div className={styles.brandContainer}>
              {group.brands.map((brand) => (
                <BrandItem
                  noShadow
                  equipmentConfigurator
                  key={`brand-${brand.id}`}
                  brand={brand}
                  image={`/get-image-by-path?path=${brand.image}`}
                  selectedEquipments={
                    values.equipments?.filter(
                      (equipment) =>
                        equipment.brand_id === brand.id &&
                        brand.equipments.some((e) => e.id === equipment.id),
                    ) || []
                  }
                  handleAddEquipment={handleAddEquipmentForConfig}
                  handleRemoveAllEquipments={handleRemoveAllEquipments}
                  equipmentBeingConfigured={equipmentBeingConfigured}
                  allEquipments={equipments}
                  allSelectedEquipments={values.equipments}
                  handleAddConfiguredEquipment={handleAddConfiguredEquipment}
                  onEquipmentChange={handleEquipmentChange}
                  onClickOutside={() => setEquipmentBeingConfigured(null)}
                  disabled={nonEditableTemplateOrPricingRestricted}
                />
              ))}

              {group.unbranded.map((equipment) => (
                <EquipmentItem
                  noShadow
                  equipmentConfigurator
                  key={`equipment-${equipment.id}`}
                  equipment={equipment}
                  image={`/get-image-by-path?path=${equipment.equipment_image}`}
                  handleAddEquipment={handleAddEquipmentForConfig}
                  handleRemoveAllEquipments={handleRemoveAllEquipments}
                  equipmentBeingConfigured={equipmentBeingConfigured}
                  allEquipments={equipments}
                  selectedEquipment={values?.equipments?.filter(
                    (e) => e.id === equipment.id,
                  )}
                  allSelectedEquipments={values?.equipments}
                  handleAddConfiguredEquipment={handleAddConfiguredEquipment}
                  onEquipmentChange={handleEquipmentChange}
                  onClickOutside={() => setEquipmentBeingConfigured(null)}
                  disabled={nonEditableTemplateOrPricingRestricted}
                />
              ))}
            </div>

            {group.name === "gateway" && (
              <div key="other-gateway" className={styles.brandContainer}>
                <TextInput
                  label="Other"
                  name="other_gateway"
                  placeholder="Gateway name"
                  value={values.other_gateway}
                  onChange={handleChange}
                  disabled={nonEditableTemplateOrPricingRestricted}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Equipments;
